import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import PriorityHighRoundedIcon from '@mui/icons-material/PriorityHighRounded';
import QuestionMarkRoundedIcon from '@mui/icons-material/QuestionMarkRounded';
import ReplayIcon from '@mui/icons-material/Replay';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  List,
  ListItem,
  ListItemText,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { Gauge, gaugeClasses } from '@mui/x-charts/Gauge';
import { DateTime } from 'luxon';
import React, { useEffect, useState } from 'react';
import PublishedActuatorPlanChart from './PublishedActuatorPlanChart';

// Function to get the appropriate icon based on status
const getStatusIcon = (status) => {
  switch (status) {
    case 'Error':
      return <ErrorRoundedIcon color="error" />;
    case 'Warning':
      return <WarningRoundedIcon color="warning" />;
    case 'Success':
      return <CheckCircleRoundedIcon color="success" />;
    case 'Critical':
      return <PriorityHighRoundedIcon color="error" />;
    case 'Unknown':
      return <QuestionMarkRoundedIcon color="gray" />;
    default:
      return null;
  }
};

const getModelStatusContent = (model, timeKey) => {
  if (!model) return null;

  const timeLabel = timeKey === 'ModelRefreshTime' ? 'Refreshed' : 'Updated';

  if (model.Status === 'Success') {
    return null;
  } else if (model.Status === 'Error') {
    return (
      <>
        {model.Errors && model.Errors.length > 0 && (
          <List>
            {model.Errors.map((error, index) => (
              <ListItem gutterBottom key={index}>
                <ListItemText
                  primary={error.error_type}
                  primaryTypographyProps={{
                    variant: 'body1',
                    color: 'text.secondary',
                    fontSize: '0.8rem',
                  }}
                  secondary={error.message}
                  secondaryTypographyProps={{
                    variant: 'body1',
                    color: 'text.primary',
                  }}
                >
                  {typeof error.message === 'string'
                    ? error.error_type + ': ' + error.message
                    : JSON.stringify(error.message)}
                </ListItemText>
                {/* {error.op && (
                  <Typography variant="caption">
                    Operation: {typeof error.op === 'string' ? error.op : JSON.stringify(error.op)}
                  </Typography>
                )}
                {error.error_type && (
                  <Typography variant="caption">
                    Type: {typeof error.error_type === 'string' ? error.error_type : JSON.stringify(error.error_type)}
                  </Typography>
                )}
                {error.account_id && (
                  <Typography variant="caption">
                    Account ID: {error.account_id.toString()}
                  </Typography>
                )}
                {error.subscription_id && (
                  <Typography variant="caption">
                    Subscription ID: {error.subscription_id.toString()}
                  </Typography>
                )}
                {error.building_id && (
                  <Typography variant="caption">
                    Building ID: {error.building_id.toString()}
                  </Typography>
                )} */}
              </ListItem>
            ))}
          </List>
        )}
      </>
    );
  } else {
    return (
      <>
        {model.Warnings && model.Warnings.length > 0 && (
          <ul>
            {model.Warnings.map((warning, index) => (
              <li key={index}>
                <Typography variant="body2">
                  {typeof warning.message === 'string'
                    ? warning.message
                    : JSON.stringify(warning.message)}
                </Typography>
                {/* {warning.op && (
                  <Typography variant="caption">
                    Operation: {typeof warning.op === 'string' ? warning.op : JSON.stringify(warning.op)}
                  </Typography>
                )}
                {warning.warning_type && (
                  <Typography variant="caption">
                    Type: {typeof warning.warning_type === 'string' ? warning.warning_type : JSON.stringify(warning.warning_type)}
                  </Typography>
                )}
                {warning.account_id && (
                  <Typography variant="caption">
                    Account ID: {warning.account_id.toString()}
                  </Typography>
                )}
                {warning.subscription_id && (
                  <Typography variant="caption">
                    Subscription ID: {warning.subscription_id.toString()}
                  </Typography>
                )}
                {warning.building_id && (
                  <Typography variant="caption">
                    Building ID: {warning.building_id.toString()}
                  </Typography>
                )} */}
              </li>
            ))}
          </ul>
        )}
      </>
    );
  }
};

const formatActivationIntervals = (intervals) => {
  return intervals
    ?.filter((interval) => interval.setpoint === 100) // Only include intervals where setpoint === 100
    ?.map((interval, index) => {
      const start = DateTime.fromISO(interval.interval_start);
      const end = DateTime.fromISO(interval.interval_end);

      const startDay = start.toFormat('cccc'); // Start day, e.g., "Monday"
      const startDate = start.toFormat('MMM dd, yyyy'); // Start date, e.g., "Nov 20, 2024"
      const startTime = start.toFormat('HH:mm'); // Start time, e.g., "08:00"

      const endDay = end.toFormat('cccc'); // End day, e.g., "Monday"
      const endDate = end.toFormat('MMM dd, yyyy'); // End date, e.g., "Nov 20, 2024"
      const endTime = end.toFormat('HH:mm'); // End time, e.g., "12:00"

      // Include day, date, and time for both start and end
      const formattedInterval = `${startDay}, ${startDate}, ${startTime} - ${endDay}, ${endDate}, ${endTime}`;

      return (
        <div
          key={index}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '8px',
          }}
        >
          <span>{formattedInterval}</span>
        </div>
      );
    });
};

const formatDaysSince = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();

  if (isNaN(date.getTime())) {
    return '-';
  }

  // Calculate the difference in time (milliseconds)
  const diffTime = Math.abs(now - date);

  // Convert time difference to days
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return `${diffDays} day(s) ago`;
};

const calculateTrueLabelPercentage = (data) => {
  // Helper function to convert ISO strings to luxon DateTime objects
  const toDate = (isoString) => DateTime.fromISO(isoString);

  // Calculate the total duration (in milliseconds) of all intervals labeled true
  let trueLabelDuration = 0;
  let totalDuration = 0;

  if (data) {
    data.forEach((interval) => {
      const start = toDate(interval.interval_start);
      const end = toDate(interval.interval_end);
      const duration = end.diff(start, 'milliseconds').milliseconds; // Duration in milliseconds

      // Add to total duration
      totalDuration += duration;

      // If label is true, add to true label duration
      if (interval.label) {
        trueLabelDuration += duration;
      }
    });
  }

  // Calculate the percentage of time where label is true
  const percentage = (trueLabelDuration / totalDuration) * 100;

  return percentage.toFixed(1); // Return the percentage rounded to one decimal place
};

const OccupancyPredictionForm = (props) => {
  const {
    open,
    onClose,
    subscription,
    initialPrediction,
    actuatorPlan,
    generateOccupancyPredictionAnalysis,
    generatedOccupancyPlan,
    isGenerating,
    occupancySourceExclusions,
    setGeneratedOccupancyPlan,
  } = props;
  const [duration, setDuration] = useState('');
  const [filterErraticPredictions, setFilterErraticPredictions] =
    useState(true);
  const [formattedIntervals, setFormattedIntervals] = useState([]);
  const [occupancyPercentage, setOccupancyPercentage] = useState(0);

  const activationIntervals = formatActivationIntervals(
    actuatorPlan?.actuator_plan,
  );

  // Helper function to initialize exclusions based on occupancySourceExclusions
  const initializeExclusions = (sourceExclusions) => {
    const defaultState = {
      baseline: false,
      flow: false,
      power: false,
      temperature: false,
    };

    // Update state based on the presence of strings in occupancySourceExclusions
    const updatedState = Object.keys(defaultState)?.reduce((state, key) => {
      state[key] = sourceExclusions?.includes(key);
      return state;
    }, {});

    return updatedState;
  };

  // Initialize the state dynamically
  const [exclusions, setExclusions] = useState(
    initializeExclusions(occupancySourceExclusions),
  );

  const handleReset = () => {
    setExclusions(initializeExclusions(occupancySourceExclusions));
    setFilterErraticPredictions(true);
    setGeneratedOccupancyPlan(null);
  };

  // Extracting the model outside of the JSX
  const model = subscription?.State?.MachineLearning?.MachineLearningInfo?.find(
    (model) => model.Name === 'PredictiveOccupancyDetection',
  );

  const formatOccupancyIntervals = (intervals) => {
    return intervals
      ?.filter((interval) => interval.label) // Only include intervals where label is true
      ?.map((interval, index) => {
        const start = DateTime.fromISO(interval.interval_start);
        const end = DateTime.fromISO(interval.interval_end);

        const startDay = start.toFormat('cccc'); // Start day, e.g., "Monday"
        const startDate = start.toFormat('MMM dd, yyyy'); // Start date, e.g., "Nov 20, 2024"
        const startTime = start.toFormat('HH:mm'); // Start time, e.g., "08:00"

        const endDay = end.toFormat('cccc'); // End day, e.g., "Monday"
        const endDate = end.toFormat('MMM dd, yyyy'); // End date, e.g., "Nov 20, 2024"
        const endTime = end.toFormat('HH:mm'); // End time, e.g., "12:00"

        // Include day, date, and time for both start and end
        const formattedInterval = `${startDay}, ${startDate}, ${startTime} - ${endDay}, ${endDate}, ${endTime}`;

        return (
          <div
            key={index}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '8px',
            }}
          >
            <span>{formattedInterval}</span>
          </div>
        );
      });
  };

  useEffect(() => {
    if (generatedOccupancyPlan) {
      setFormattedIntervals(formatOccupancyIntervals(generatedOccupancyPlan));
      const percentage = calculateTrueLabelPercentage(generatedOccupancyPlan);
      setOccupancyPercentage(percentage);
    } else if (!generatedOccupancyPlan && initialPrediction) {
      setFormattedIntervals(
        formatOccupancyIntervals(initialPrediction.occupancy_plan),
      );
      const percentage = calculateTrueLabelPercentage(
        initialPrediction.occupancy_plan,
      );
      setOccupancyPercentage(percentage);
    }
  }, [generatedOccupancyPlan, initialPrediction]);

  const handleExclusionChange = (name) => {
    setExclusions({
      ...exclusions,
      [name]: !exclusions[name],
    });
  };

  const formattedExclusions = Object.keys(exclusions).filter(
    (key) => exclusions[key],
  );

  const handleSubmit = async (e) => {
    e.preventDefault(); // Prevent default form submission behavior
    const formData = {
      duration,
      filterErraticPredictions,
      exclusions: formattedExclusions,
    };
    await generateOccupancyPredictionAnalysis(formData); // Pass the form data to the parent
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl">
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          {subscription?.JobSubscriptionId} - {subscription?.DisplayName}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Tooltip
              title="Generate the Occupancy Prediction schedule"
              placement="top"
            >
              <Button
                variant="standard"
                onClick={handleReset}
                sx={{
                  textTransform: 'none',
                  ml: 1,
                }}
              >
                Reset
              </Button>
              <Button
                onClick={handleSubmit}
                endIcon={
                  generatedOccupancyPlan && !isGenerating ? (
                    <ReplayIcon />
                  ) : null
                }
                variant="contained"
                disableElevation
                loading={props.loading}
                {...props}
              >
                {isGenerating ? (
                  <>
                    Generating...{' '}
                    <CircularProgress
                      size={20}
                      color="inherit"
                      sx={{ ml: 2 }}
                    />
                  </>
                ) : generatedOccupancyPlan ? (
                  'Regenerate'
                ) : (
                  'Generate'
                )}
              </Button>
            </Tooltip>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Card variant="outlined" sx={{ padding: 2 }}>
              <ListItemText
                primary="Predictive Occupancy Detection Model Type"
                primaryTypographyProps={{
                  variant: 'label',
                  fontSize: '0.8rem',
                  color: 'text.secondary',
                }}
                secondary={subscription ? `${model.ModelType}` : ''}
                secondaryTypographyProps={{
                  variant: 'body1',
                  color: 'text.primary',
                  mt: 1,
                }}
              />
            </Card>
          </Grid>
          <Grid item xs={4}>
            <Card variant="outlined" sx={{ padding: 2 }}>
              <ListItemText
                primary="Predictive Occupancy Detection Model Refresh Time"
                primaryTypographyProps={{
                  variant: 'label',
                  fontSize: '0.8rem',
                  color: 'text.secondary',
                }}
                secondary={
                  <Box sx={{ display: 'flex', mt: 1 }}>
                    <Tooltip
                      title={getModelStatusContent(model)}
                      placement="top"
                    >
                      {getStatusIcon(model?.Status)}
                    </Tooltip>
                    <Typography variant="subtitle" sx={{ ml: 1 }}>
                      Refreshed {formatDaysSince(model?.ModelRefreshTime)}
                    </Typography>
                  </Box>
                } // Use the helper function here
                secondaryTypographyProps={{
                  variant: 'body1',
                  color: 'text.primary',
                }}
              />
            </Card>
          </Grid>
          <Grid item xs={4}>
            <Card variant="outlined" sx={{ padding: 2 }}>
              <ListItemText
                primary="Predictive Occupancy Detection Model Update Time"
                primaryTypographyProps={{
                  variant: 'label',
                  fontSize: '0.8rem',
                  color: 'text.secondary',
                }}
                secondary={
                  <Box sx={{ display: 'flex', mt: 1 }}>
                    <Tooltip
                      title={getModelStatusContent(model)}
                      placement="top"
                    >
                      {getStatusIcon(model?.Status)}
                    </Tooltip>
                    <Typography variant="subtitle" sx={{ ml: 1 }}>
                      Updated {formatDaysSince(model?.ModelRefreshTime)}
                    </Typography>
                  </Box>
                } // Use the helper function here
                secondaryTypographyProps={{
                  variant: 'body1',
                  color: 'text.primary',
                }}
              />
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card variant="outlined" sx={{ padding: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  position: 'relative',
                }}
              >
                <Typography gutterBottom sx={{ color: 'text.secondary' }}>
                  Analysis Input
                </Typography>
                <Tooltip
                  arrow
                  title="Input the minimum duration of an occupancy event in hours. Shorter events will be filtered out. If Filter Erratic Prediction is checked, this parameter is ignored. Data sources can also be excluded from the occupancy prediction."
                  placement="top"
                >
                  <InfoRoundedIcon sx={{ ml: 1, color: 'lightgray' }} />
                </Tooltip>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <TextField
                      type="number"
                      label="Minimum Duration"
                      inputProps={{ min: 0, step: 1 }}
                      value={duration}
                      onChange={(e) => setDuration(e.target.value)}
                      fullWidth
                      sx={{ mt: 1 }}
                      optional
                    />
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  {['baseline', 'flow', 'power', 'temperature', 'co2'].map(
                    (option) => (
                      <Tooltip
                        arrow
                        title={`If checked, ${option} will be excluded from the analysis.`}
                        placement="top"
                      >
                        <FormControlLabel
                          key={option}
                          sx={{ ml: 1 }}
                          control={
                            <Switch
                              checked={exclusions[option]}
                              onChange={() => handleExclusionChange(option)}
                              size="small"
                              // sx={{
                              //   '& .MuiSwitch-switchBase.Mui-checked': {
                              //     color: 'error.main',
                              //   },
                              //   '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              //     backgroundColor: 'error.main',
                              //   },
                              // }}
                            />
                          }
                          label={option}
                        />
                      </Tooltip>
                    ),
                  )}
                  <Tooltip
                    arrow
                    title="If Checked, the algorithm will filter out erratic predictions."
                    placement="top"
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          checked={filterErraticPredictions}
                          onChange={(e) =>
                            setFilterErraticPredictions(e.target.checked)
                          }
                          size="small"
                          sx={{
                            // '& .MuiSwitch-switchBase.Mui-checked': {
                            //   color: '#52b202',
                            // },
                            // '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            //   backgroundColor: '#52b202',
                            // },
                            ml: 2,
                          }}
                        />
                      }
                      label="Filter Erratic Predictions"
                    />
                  </Tooltip>
                </Grid>
              </Grid>
            </Card>
          </Grid>

          <Grid item xs={6}>
            <Card variant="outlined" sx={{ padding: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  position: 'relative',
                  mb: 4,
                }}
              >
                <Typography gutterBottom sx={{ color: 'text.secondary' }}>
                  Occupied Plan
                </Typography>
                <Tooltip
                  arrow
                  title="Upon generating the analysis, the occupancy plan will be shown here."
                  placement="top"
                >
                  <InfoRoundedIcon sx={{ ml: 1, color: 'lightgray' }} />
                </Tooltip>
              </Box>
              {formattedIntervals?.length > 0 ? (
                formattedIntervals?.map((interval, index) => (
                  <Typography key={index} variant="body1">
                    {interval}
                  </Typography>
                ))
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  <Typography sx={{ fontSize: 14 }}>
                    Generate to view plan.
                  </Typography>
                </Box>
              )}
            </Card>
          </Grid>
          <Grid item xs={6}>
            <Card variant="outlined" sx={{ padding: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  position: 'relative',
                  mb: 4,
                }}
              >
                <Typography gutterBottom sx={{ color: 'text.secondary' }}>
                  Scheduled Activations
                </Typography>
                <Tooltip
                  arrow
                  title="The schedule of activations for the actuator plan"
                  placement="top"
                >
                  <InfoRoundedIcon sx={{ ml: 1, color: 'lightgray' }} />
                </Tooltip>
              </Box>
              {activationIntervals?.length > 0 ? (
                activationIntervals?.map((interval, index) => (
                  <Typography key={index} variant="body1">
                    {interval}
                  </Typography>
                ))
              ) : (
                <Typography variant="body1">
                  No scheduled activations found
                </Typography>
              )}
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card variant="outlined" sx={{ padding: 2 }}>
              <PublishedActuatorPlanChart
                actuatorPlan={actuatorPlan}
                occupancyPrediction={initialPrediction}
                generatedOccupancyPrediction={generatedOccupancyPlan}
              />
            </Card>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default OccupancyPredictionForm;
